import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel,
  IconButton, Select, MenuItem, InputLabel, FormControl, Tabs, Tab, Chip, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import BusinessIcon from '@material-ui/icons/Business';
import CloseIcon from '@material-ui/icons/Close';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PeopleIcon from '@material-ui/icons/People';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import LockIcon from '@material-ui/icons/Lock';
import RefreshIcon from '@material-ui/icons/Refresh';
import { COLORS } from '../../theme';
import { geocodeSearch } from '../../utils/mapboxApi';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';
import { uiVariantMeta } from '../../utils/manageHelpers';

const API_BASE = '/planner-api';

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes slideInUp': {
      '0%': { opacity: 0, transform: 'translateY(20px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 22,
    background: 'var(--adm-header-bg, #e2f1e9)',
    border: '1px solid var(--adm-header-border, #bfe0d1)',
    borderRadius: 14,
    padding: '18px 22px',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  titleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '1.72rem',
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  addBtn: {
    background: 'var(--adm-brand, #2f5d50)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.82rem',
    borderRadius: 11,
    padding: '9px 22px',
    boxShadow: '0 4px 16px rgba(47,93,80,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'var(--adm-brand-dark, #274c41)',
      boxShadow: '0 6px 20px rgba(47,93,80,0.4)',
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.25s ease',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  // Cartões (vista alternativa à tabela)
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  orgCard: {
    position: 'relative',
    background: 'var(--adm-card, #fff)',
    borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.06))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    padding: '18px 20px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(52,71,103,0.12)' },
  },
  cardEditBtn: {
    position: 'absolute', top: 14, right: 14, width: 30, height: 30,
    background: 'rgba(47,93,80,0.06)', borderRadius: 8,
    '&:hover': { background: 'rgba(47,93,80,0.14)' },
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, paddingRight: 34 },
  cardName: { fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardBadges: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  cardProfile: { background: 'var(--adm-section-bg, #f3f8f5)', border: '1px solid var(--adm-section-border, #e2efe8)', borderRadius: 11, padding: '11px 13px' },
  cardProfileLabel: { fontSize: '0.6rem', fontWeight: 700, color: '#6b8a80', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  groupBadge: { fontSize: '0.62rem', fontWeight: 700, background: 'rgba(99,102,241,0.1)', color: '#4338ca', padding: '3px 9px', borderRadius: 999 },
  variantBadge: { fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap' },
  noProfile: { fontSize: '0.74rem', fontWeight: 600, color: '#b45309' },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--adm-input-bg, #ffffff)',
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 12,
    padding: '0 14px',
    height: 44,
    flex: 1,
    maxWidth: 380,
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
    '&:focus-within': {
      borderColor: 'var(--adm-brand, #2f5d50)',
      boxShadow: '0 0 0 4px var(--adm-brand-soft, rgba(47,93,80,0.08)), 0 1px 3px rgba(0,0,0,0.03)',
    },
  },
  tableCard: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    overflow: 'visible', // cabeçalho sticky
    animation: '$slideInUp 0.4s ease-out',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 20px',
    fontSize: '0.66rem',
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))',
    background: 'var(--adm-thead-bg, #f8fafc)',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: {
    padding: '15px 20px',
    fontSize: '0.84rem',
    color: COLORS.text,
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))',
  },
  tableRow: {
    transition: 'background 0.15s',
    '&:hover': { background: 'var(--adm-hover, #f8fafc)' },
    '&:last-child td': { borderBottom: 'none' },
  },
  clientCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  clientDot: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.72rem',
    color: '#fff',
    flexShrink: 0,
  },
  badge: {
    display: 'inline-block',
    padding: '4px 11px',
    borderRadius: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badgeActive: {
    background: 'rgba(34,197,94,0.08)',
    color: '#16a34a',
  },
  badgeInactive: {
    background: 'rgba(239,68,68,0.08)',
    color: '#dc2626',
  },
  typeBadge: {
    display: 'inline-block',
    padding: '4px 11px',
    borderRadius: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  editBtn: {
    color: 'var(--adm-text-muted, #94a3b8)',
    transition: 'all 0.2s',
    '&:hover': { color: 'var(--adm-brand, #2f5d50)', background: 'rgba(47,93,80,0.06)' },
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      padding: '8px 4px',
      width: 720,
      maxWidth: '94vw',
      boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px 12px',
  },
  dialogTitle: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: COLORS.text,
  },
  dialogContent: {
    padding: '12px 28px 20px',
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  modulesSection: {
    marginTop: 8,
    padding: 16,
    background: 'var(--adm-bg, #f8fafc)',
    borderRadius: 12,
    border: '1px solid var(--adm-border, rgba(0,0,0,0.04))',
  },
  modulesSectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 2,
  },
  moduleCheck: {
    borderRadius: 8,
    padding: '2px 4px',
    transition: 'background 0.15s',
    '&:hover': { background: 'rgba(59,130,246,0.04)' },
    '& .MuiFormControlLabel-label': {
      fontSize: '0.82rem',
      fontWeight: 500,
    },
  },
  dialogActions: {
    padding: '12px 28px 20px',
    gap: 10,
  },
  cancelBtn: {
    color: COLORS.textMuted,
    fontWeight: 500,
    borderRadius: 10,
    padding: '8px 20px',
    '&:hover': { background: 'var(--adm-hover, #f1f5f9)' },
  },
  saveBtn: {
    background: 'var(--adm-brand, #2f5d50)',
    color: '#fff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 28px',
    boxShadow: '0 4px 12px rgba(47,93,80,0.3)',
    '&:hover': {
      background: 'var(--adm-brand-dark, #274c41)',
    },
    '&:disabled': {
      background: '#e2e8f0',
      color: '#94a3b8',
      boxShadow: 'none',
    },
  },
  originSection: {
    marginTop: 8,
    padding: 16,
    background: 'var(--adm-bg, #f8fafc)',
    borderRadius: 12,
    border: '1px solid var(--adm-border, rgba(0,0,0,0.04))',
  },
  originSuggestions: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    border: '1px solid var(--adm-input-border, #e2e8f0)',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    background: 'var(--adm-card, #fff)',
    maxHeight: 160,
    overflowY: 'auto',
    '& li': {
      padding: '8px 12px',
      fontSize: '0.8rem',
      color: 'var(--adm-text, #334155)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      '&:hover': { background: 'var(--adm-hover, #f1f5f9)' },
    },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
  },
  emptyRow: {
    textAlign: 'center',
    padding: 40,
    color: COLORS.textMuted,
    fontSize: '0.85rem',
  },
  modulesCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 22,
    borderRadius: 6,
    background: 'var(--adm-hover, #f1f5f9)',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: COLORS.textSecondary,
  },
  // ── Separadores Utilizadores / Frota dentro do diálogo ──
  ovKpis: { display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  ovKpi: { flex: 1, minWidth: 110, borderRadius: 12, padding: '10px 14px', background: 'var(--adm-thead-bg, #f8fafc)', border: '1px solid rgba(0,0,0,0.04)' },
  ovKpiVal: { fontSize: '1.3rem', fontWeight: 800, color: COLORS.text, lineHeight: 1 },
  ovKpiLbl: { fontSize: '0.62rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginTop: 5 },
  ovTable: { width: '100%', borderCollapse: 'collapse' },
  ovTh: { textAlign: 'left', padding: '9px 12px', fontSize: '0.6rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'var(--adm-thead-bg, #f8fafc)', position: 'sticky', top: 0 },
  ovTd: { padding: '9px 12px', fontSize: '0.78rem', color: COLORS.text, borderBottom: '1px solid rgba(0,0,0,0.03)' },
  ovRow: { '&:hover': { background: 'var(--adm-hover, #f8fafc)' }, '&:last-child td': { borderBottom: 'none' } },
  ovBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: '0.64rem', fontWeight: 700 },
  ovOn: { background: '#e9f7ec', color: '#15803d' },
  ovOff: { background: '#f1f5f9', color: '#64748b' },
  ovMono: { fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontSize: '0.72rem', color: COLORS.textSecondary },
  ovEmpty: { padding: '30px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '0.8rem' },
  ovScroll: { maxHeight: '48vh', overflow: 'auto', border: '1px solid rgba(0,0,0,0.05)', borderRadius: 12 },
}));

const TYPE_COLORS = {
  ecoogps: { bg: 'rgba(59,130,246,0.08)', color: '#3b82f6' },
  eurostral: { bg: 'rgba(245,158,11,0.08)', color: '#f59e0b' },
  direct: { bg: 'rgba(99,102,241,0.08)', color: '#6366f1' },
};

const CLIENT_COLORS = ['#3b82f6', '#22c55e', '#6366f1', '#f59e0b', '#ec4899', '#ef4444', '#14b8a6', '#8b5cf6'];
// Estado inicial do formulário (o diálogo abre sempre em modo edição e é
// preenchido pelo openEdit — isto é só o valor de arranque do useState).
const EMPTY_CLIENT = { name: '', slug: '', type: 'direct', isActive: true, defaultOrigin: null, groupId: '', profileRefId: '' };

// ── Rótulos/formatos p/ os separadores Utilizadores e Frota (Organização 360
//    integrada no diálogo de edição) ──────────────────────────────────────
const ROLE_LABEL = { super_admin: 'Super Admin', admin: 'Admin', user: 'Utilizador' };
const PERM_LABEL = { viewer: 'Visualizador', operator: 'Operador', manager: 'Gestor' };
const ROLE_COLOR = { super_admin: '#7c3aed', admin: '#2563eb', user: '#64748b' };
const fmtDateT = (iso) => { if (!iso) return '—'; const d = new Date(iso); return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
const fmtAgoT = (iso) => { if (!iso) return null; const ms = Date.now() - new Date(iso).getTime(); if (!Number.isFinite(ms) || ms < 0) return null; const m = Math.round(ms / 60000); if (m < 1) return 'agora'; if (m < 60) return `há ${m} min`; const h = Math.round(m / 60); if (h < 24) return `há ${h} h`; return `há ${Math.round(h / 24)} d`; };

// Slug = identificador técnico interno (minúsculas, sem espaços/acentos),
// gerado automaticamente a partir do nome. Estável e usado nas ligações internas.
const slugify = (s) => (s || '').toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

function AdminClients({ token, navParams, onNavigate }) {
  const classes = useStyles();
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useAdminView('clients');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);
  // Organização 360 integrada no diálogo: separador ativo + dados (users/frota).
  const [activeTab, setActiveTab] = useState('dados');
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [originQuery, setOriginQuery] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [originSearching, setOriginSearching] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [cRes, gRes, pRes] = await Promise.all([
        fetch(`${API_BASE}/clients`, { headers }),
        fetch(`${API_BASE}/client-groups`, { headers }),
        fetch(`${API_BASE}/profiles`, { headers }),
      ]);
      if (cRes.ok) setClients(await cRes.json());
      if (gRes.ok) setGroups(await gRes.json());
      if (pRes.ok) setProfiles(await pRes.json());
    } catch (err) {
      console.error('Clients load error:', err);
    }
    setLoading(false);
  }

  // NOTA: não existe `openCreate`. As Organizações nascem do login (ver o
  // comentário no cabeçalho). O diálogo abre sempre em modo edição.

  function openEdit(client) {
    setEditing(client);
    setForm({
      name: client.name,
      slug: client.slug,
      type: client.type,
      isActive: client.isActive,
      defaultOrigin: client.settings?.defaultOrigin || null,
      groupId: client.group?.id || '',
      profileRefId: client.profileRefId || '',
    });
    setActiveTab('dados');
    setDialogOpen(true);
    loadOverview(client.id);
  }

  // Carrega a visão 360 da organização (utilizadores + frota) para os
  // separadores do diálogo. Endpoint único /clients/:id/overview.
  async function loadOverview(id) {
    setOverview(null); setOvLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/overview`, { headers: { Authorization: `Bearer ${token}` } });
      setOverview(res.ok ? await res.json() : null);
    } catch (e) { console.error('Overview error:', e); setOverview(null); }
    setOvLoading(false);
  }

  // Abre a edição automaticamente quando se chega por um link (ex.: clicar na
  // Entidade/Organização no ecrã de Utilizadores). Consome o parâmetro uma vez.
  const navConsumedRef = React.useRef(null);
  useEffect(() => {
    const id = navParams?.editClientId;
    if (!id || !clients.length || navConsumedRef.current === id) return;
    const c = clients.find(x => String(x.id) === String(id));
    if (c) { navConsumedRef.current = id; openEdit(c); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navParams, clients]);

  async function handleSave() {
    setSaving(true);
    try {
      const body = { name: form.name, slug: form.slug, type: form.type, active: form.isActive, groupId: form.groupId || null, profileRefId: form.profileRefId || null };
      // Só edição: as Organizações nascem do login, não se criam aqui.
      const res = await fetch(`${API_BASE}/clients/${editing.id}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      if (!res.ok) { setSaving(false); return; }

      // [V3] Os módulos NÃO se atribuem à Organização — vêm do Perfil que ela
      // aponta. Removida a escrita de client_modules (modelo antigo).

      // Save defaultOrigin in client settings
      const currentSettings = editing?.settings || {};
      const newSettings = { ...currentSettings };
      if (form.defaultOrigin) {
        newSettings.defaultOrigin = form.defaultOrigin;
      } else {
        delete newSettings.defaultOrigin;
      }
      await fetch(`${API_BASE}/clients/${editing.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ settings: newSettings }),
      });

      setDialogOpen(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      console.error('Save error:', err);
    }
    setSaving(false);
  }

  // Geocode search for default origin
  useEffect(() => {
    if (!originQuery || originQuery.length < 3) {
      setOriginSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setOriginSearching(true);
      const results = await geocodeSearch(originQuery);
      setOriginSuggestions(results);
      setOriginSearching(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [originQuery]);

  function selectOrigin(item) {
    setForm(prev => ({ ...prev, defaultOrigin: { name: item.name, coordinates: item.coordinates } }));
    setOriginQuery('');
    setOriginSuggestions([]);
  }

  function clearOrigin() {
    setForm(prev => ({ ...prev, defaultOrigin: null }));
    setOriginQuery('');
    setOriginSuggestions([]);
  }

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
      c.slug.toLowerCase().includes(q) ||
      (c.ecoogpsClientName || '').toLowerCase().includes(q);
  });

  // Metadados de cada linha/cartão. O Perfil é a fonte de verdade dos módulos
  // e da variante — a antiga coluna "Modulos" lia client_modules (modelo
  // legado) e divergia do que o cliente realmente tem.
  function rowMeta(client) {
    const profile = profiles.find(p => String(p.id) === String(client.profileRefId)) || null;
    const hasReal = !!client.ecoogpsClientName;
    return {
      // Destaca o nome REAL dado no Planner (clients.name); o nome que vem do
      // EcooGPS fica como referência secundária no subtítulo.
      displayName: client.name || client.ecoogpsClientName,
      sub: hasReal
        ? `EcooGPS: ${client.ecoogpsClientName}${client.ecoogpsClientId ? ` · #${client.ecoogpsClientId}` : ''}`
        : `${client.slug}${client.externalId ? ` · user ext ${client.externalId} · sem login ainda` : ''}`,
      profile,
      vm: profile ? uiVariantMeta(profile.extraSettings?.uiVariant) : null,
      moduleCount: (profile?.moduleKeys || []).length,
      tc: TYPE_COLORS[client.type] || TYPE_COLORS.direct,
    };
  }

  if (loading) {
    return <Box className={classes.loading}><CircularProgress size={36} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>;
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <Box className={classes.titleIcon}>
            <BusinessIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.title}>Entidades / Organizações</Typography>
            <Typography className={classes.subtitle}>{clients.length} entidades/organizações registadas · criadas automaticamente no 1.º login</Typography>
          </Box>
        </Box>
        {/* NÃO há botão "Nova Organização" de propósito: as Organizações não se
            criam à mão — nascem sozinhas quando alguém entra pela 1.ª vez (o
            login regista a empresa vinda do EcooGPS). Uma Organização criada
            aqui ficava órfã: o login procura por external_id/ecoogps_client_id,
            não a encontrava, e criava outra. Aqui CURA-SE: Perfil, Grupo, nome. */}
      </Box>

      <AdminHint
        nodes={[{ text: 'Grupo', tone: 'slate' }, { text: 'Organização', tone: 'green' }, { text: 'Perfil', tone: 'indigo' }]}
        labels={['agrega', 'tem o seu']}
        note="As Organizações não se criam aqui — nascem sozinhas quando alguém entra no planner pela 1.ª vez (o login regista a empresa vinda do EcooGPS). O trabalho aqui é curá-las: dar o Perfil certo (que define os ecrãs e a configuração), o Grupo, e corrigir o nome. Cada Organização é dona dos seus dados."
      />

      <Box className={classes.toolbar}>
        <Box className={classes.searchBox}>
          <SearchIcon style={{ color: 'var(--adm-text-muted, #94a3b8)', fontSize: 19 }} />
          <input
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.85rem', color: COLORS.text, background: 'transparent' }}
            placeholder="Pesquisar por nome ou slug..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>
        <AdminViewToggle view={view} onChange={setView} />
      </Box>

      {filtered.length === 0 ? (
        <Box className={classes.tableCard}>
          <Typography className={classes.emptyRow}>Nenhuma organização encontrada</Typography>
        </Box>
      ) : view === 'table' ? (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Entidade / Organização</th>
                <th className={classes.th}>Grupo</th>
                <th className={classes.th}>Perfil</th>
                <th className={classes.th}>Tipo</th>
                <th className={classes.th}>Estado</th>
                <th className={classes.th} style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((client, idx) => {
                const m = rowMeta(client);
                return (
                  <tr key={client.id} className={classes.tableRow}>
                    <td className={classes.td}>
                      <Box className={classes.clientCell}>
                        <Box className={classes.clientDot} style={{ background: CLIENT_COLORS[idx % CLIENT_COLORS.length] }}>
                          {m.displayName.charAt(0).toUpperCase()}
                        </Box>
                        <Box>
                          <Typography style={{ fontWeight: 600, fontSize: '0.85rem', color: COLORS.text }}>{m.displayName}</Typography>
                          <Typography style={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{m.sub}</Typography>
                        </Box>
                      </Box>
                    </td>
                    <td className={classes.td}>
                      {client.group?.name
                        ? <span className={classes.groupBadge}>{client.group.name}</span>
                        : <span style={{ color: COLORS.textMuted }}>—</span>}
                    </td>
                    <td className={classes.td}>
                      {m.profile ? (
                        <Box>
                          <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {m.profile.name}
                            {m.vm && <span className={classes.variantBadge} style={{ background: m.vm.bg, color: m.vm.color, borderColor: m.vm.border }}>{m.vm.icon} {m.vm.short}</span>}
                          </Typography>
                          <Typography style={{ fontSize: '0.68rem', color: COLORS.textMuted }}>{m.moduleCount} módulos</Typography>
                        </Box>
                      ) : (
                        <span className={classes.noProfile}>⚠ sem perfil</span>
                      )}
                    </td>
                    <td className={classes.td}>
                      <span className={classes.typeBadge} style={{ background: m.tc.bg, color: m.tc.color }}>{client.type}</span>
                    </td>
                    <td className={classes.td}>
                      <span className={`${classes.badge} ${client.isActive ? classes.badgeActive : classes.badgeInactive}`}>
                        {client.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <IconButton size="small" onClick={() => openEdit(client)} className={classes.editBtn}>
                        <EditIcon style={{ fontSize: 17 }} />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      ) : (
        <Box className={classes.cardsGrid}>
          {filtered.map((client, idx) => {
            const m = rowMeta(client);
            return (
              <Box key={client.id} className={classes.orgCard}>
                <IconButton size="small" onClick={() => openEdit(client)} className={classes.cardEditBtn}>
                  <EditIcon style={{ fontSize: 15, color: 'var(--adm-brand, #2f5d50)' }} />
                </IconButton>
                <Box className={classes.cardTop}>
                  <Box className={classes.clientDot} style={{ background: CLIENT_COLORS[idx % CLIENT_COLORS.length], width: 42, height: 42, fontSize: '0.85rem' }}>
                    {m.displayName.charAt(0).toUpperCase()}
                  </Box>
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Typography className={classes.cardName}>{m.displayName}</Typography>
                    <Typography style={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{m.sub}</Typography>
                  </Box>
                </Box>

                <Box className={classes.cardBadges}>
                  <span className={`${classes.badge} ${client.isActive ? classes.badgeActive : classes.badgeInactive}`}>
                    {client.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                  <span className={classes.typeBadge} style={{ background: m.tc.bg, color: m.tc.color }}>{client.type}</span>
                  {client.group?.name && <span className={classes.groupBadge}>{client.group.name}</span>}
                </Box>

                <Box className={classes.cardProfile}>
                  <Typography className={classes.cardProfileLabel}>Perfil</Typography>
                  {m.profile ? (
                    <>
                      <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {m.profile.name}
                        {m.vm && <span className={classes.variantBadge} style={{ background: m.vm.bg, color: m.vm.color, borderColor: m.vm.border }}>{m.vm.icon} {m.vm.short}</span>}
                      </Typography>
                      <Typography style={{ fontSize: '0.7rem', color: 'var(--adm-brand-mid, #5a8577)', marginTop: 2 }}>{m.moduleCount} módulos ligados</Typography>
                    </>
                  ) : (
                    <span className={classes.noProfile}>⚠ sem perfil</span>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} className={classes.dialog} maxWidth={false}
        PaperProps={{ style: { width: activeTab === 'dados' ? 720 : 980 } }}>
        <Box className={classes.dialogHeader}>
          <Box>
            <Typography className={classes.dialogTitle}>
              {editing?.name || 'Editar Organização'}
            </Typography>
            {editing?.ecoogpsClientId && (
              <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-text-muted, #94a3b8)', fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", marginTop: 2 }}>
                EcooGPS #{editing.ecoogpsClientId}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)} style={{ color: 'var(--adm-text-muted, #94a3b8)' }}>
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary" textColor="primary" variant="fullWidth"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.07)', margin: '0 28px' }}>
          <Tab value="dados" label="Dados" icon={<BusinessIcon style={{ fontSize: 18 }} />} style={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }} />
          <Tab value="users" label={`Utilizadores${overview ? ` (${overview.users.length})` : ''}`} icon={<PeopleIcon style={{ fontSize: 18 }} />} style={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }} />
          <Tab value="fleet" label={`Frota${overview ? ` (${overview.fleetMeta.count})` : ''}`} icon={<LocalShippingIcon style={{ fontSize: 18 }} />} style={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }} />
        </Tabs>
        <DialogContent className={classes.dialogContent}>
          {activeTab !== 'dados' && (ovLoading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><CircularProgress size={30} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>
          ) : !overview ? (
            <Box className={classes.ovEmpty}>Não foi possível carregar os dados da organização.</Box>
          ) : activeTab === 'users' ? (
            <Box>
              <Box className={classes.ovKpis}>
                <Box className={classes.ovKpi}><Typography className={classes.ovKpiVal}>{overview.users.length}</Typography><Typography className={classes.ovKpiLbl}>Utilizadores</Typography></Box>
                <Box className={classes.ovKpi}><Typography className={classes.ovKpiVal}>{overview.users.filter(u => u.isActive).length}</Typography><Typography className={classes.ovKpiLbl}>Ativos</Typography></Box>
                <Box className={classes.ovKpi}><Typography className={classes.ovKpiVal}>{overview.client.modules.length}</Typography><Typography className={classes.ovKpiLbl}>Módulos</Typography></Box>
              </Box>
              {overview.users.length === 0 ? (
                <Box className={classes.ovEmpty}>Esta organização ainda não tem utilizadores.</Box>
              ) : (
                <Box className={classes.ovScroll}>
                  <table className={classes.ovTable}>
                    <thead><tr>
                      <th className={classes.ovTh}>Nome</th><th className={classes.ovTh}>Email</th>
                      <th className={classes.ovTh}>Papel</th><th className={classes.ovTh}>Permissão</th>
                      <th className={classes.ovTh}>Estado</th><th className={classes.ovTh}>Último acesso</th>
                    </tr></thead>
                    <tbody>
                      {overview.users.map(u => (
                        <tr key={u.id} className={classes.ovRow}>
                          <td className={classes.ovTd}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <PersonIcon style={{ fontSize: 15, color: COLORS.textMuted }} />
                              <span style={{ fontWeight: 700 }}>{u.name}</span>
                              {u.clientLocked && <Tooltip title="Organização fixada"><LockIcon style={{ fontSize: 12, color: '#d97706' }} /></Tooltip>}
                            </Box>
                          </td>
                          <td className={`${classes.ovTd} ${classes.ovMono}`}>{u.email}</td>
                          <td className={classes.ovTd}><span className={classes.ovBadge} style={{ background: `${ROLE_COLOR[u.role] || '#64748b'}1a`, color: ROLE_COLOR[u.role] || '#64748b' }}>{ROLE_LABEL[u.role] || u.role}</span></td>
                          <td className={classes.ovTd}>{PERM_LABEL[u.permissionLevel] || u.permissionLevel || '—'}</td>
                          <td className={classes.ovTd}><span className={`${classes.ovBadge} ${u.isActive ? classes.ovOn : classes.ovOff}`}>{u.isActive ? 'Ativo' : 'Inativo'}</span></td>
                          <td className={`${classes.ovTd} ${classes.ovMono}`}>{fmtDateT(u.lastLoginAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Box className={classes.ovKpis}>
                <Box className={classes.ovKpi}><Typography className={classes.ovKpiVal}>{overview.fleetMeta.count}</Typography><Typography className={classes.ovKpiLbl}>Veículos</Typography></Box>
                <Box className={classes.ovKpi}><Typography className={classes.ovKpiVal}>{overview.fleetMeta.active}</Typography><Typography className={classes.ovKpiLbl}>Ativos</Typography></Box>
                <Box className={classes.ovKpi} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography className={classes.ovKpiLbl} style={{ marginTop: 0 }}>Atualizado</Typography>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.textSecondary }}>{overview.fleetMeta.lastUpdated ? (fmtAgoT(overview.fleetMeta.lastUpdated) || fmtDateT(overview.fleetMeta.lastUpdated)) : '—'}</Typography>
                </Box>
              </Box>
              {overview.fleet.length === 0 ? (
                <Box className={classes.ovEmpty}>
                  Ainda não há frota registada para esta organização.<br />
                  <span style={{ fontSize: '0.74rem' }}>Abre o ecrã <b>Frota</b> no Planner com um utilizador desta organização para a capturar automaticamente.</span>
                </Box>
              ) : (
                <Box className={classes.ovScroll}>
                  <table className={classes.ovTable}>
                    <thead><tr>
                      <th className={classes.ovTh}>Matrícula</th><th className={classes.ovTh}>Nome / Modelo</th>
                      <th className={classes.ovTh}>Motorista</th><th className={classes.ovTh}>Grupo</th>
                      <th className={classes.ovTh}>Estado</th><th className={classes.ovTh}>Última posição</th>
                    </tr></thead>
                    <tbody>
                      {overview.fleet.map(v => (
                        <tr key={v.id} className={classes.ovRow}>
                          <td className={classes.ovTd}><span style={{ fontWeight: 800 }}>{v.plate || '—'}</span></td>
                          <td className={classes.ovTd}>{v.name || '—'}{v.model ? <span style={{ color: COLORS.textMuted }}> · {v.model}</span> : ''}</td>
                          <td className={classes.ovTd}>{v.driverName || <span style={{ color: COLORS.textMuted }}>—</span>}</td>
                          <td className={classes.ovTd}>{v.groupId != null ? <Chip size="small" label={`#${v.groupId}`} style={{ height: 19, fontSize: '0.6rem' }} /> : '—'}</td>
                          <td className={classes.ovTd}><span className={`${classes.ovBadge} ${v.disabled ? classes.ovOff : classes.ovOn}`}>{v.disabled ? 'Desativado' : 'Ativo'}</span></td>
                          <td className={`${classes.ovTd} ${classes.ovMono}`}>{v.lastSeen ? fmtDateT(v.lastSeen) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          ))}
          <Box className={classes.formGrid} style={{ display: activeTab === 'dados' ? undefined : 'none' }}>
            <TextField
              label="Nome da Organização" fullWidth variant="outlined" size="small"
              value={form.name}
              onChange={e => { const name = e.target.value; setForm(f => ({ ...f, name, slug: editing ? f.slug : slugify(name) })); }}
            />
            <Box className={classes.formRow}>
              <TextField
                label="Identificador técnico (slug)" variant="outlined" size="small"
                value={form.slug} onChange={e => setForm({ ...form, slug: slugify(e.target.value) })}
                helperText="Gerado do nome — só minúsculas e hífens, sem espaços"
              />
              <FormControl variant="outlined" size="small">
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <MenuItem value="ecoogps">EcooGPS</MenuItem>
                  <MenuItem value="eurostral">Eurostral</MenuItem>
                  <MenuItem value="direct">Directo</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* Grupo/holding + referência EcooGPS (client_details) */}
            <Box className={classes.formRow}>
              <FormControl variant="outlined" size="small">
                <InputLabel>Grupo</InputLabel>
                <Select label="Grupo" value={form.groupId || ''} onChange={e => setForm({ ...form, groupId: e.target.value })}>
                  <MenuItem value=""><em>— sem grupo —</em></MenuItem>
                  {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small">
                <InputLabel>Perfil</InputLabel>
                <Select label="Perfil" value={form.profileRefId || ''} onChange={e => setForm({ ...form, profileRefId: e.target.value })}>
                  <MenuItem value=""><em>— sem perfil —</em></MenuItem>
                  {profiles.filter(p => p.scope === 'tenant').map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
              {editing?.ecoogpsClientName && (
                <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography style={{ fontSize: '0.68rem', color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>EcooGPS (client_details)</Typography>
                  <Typography style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--adm-text, #0f172a)' }}>{editing.ecoogpsClientName} <span style={{ color: 'var(--adm-text-muted, #94a3b8)', fontWeight: 400 }}>#{editing.ecoogpsClientId}</span></Typography>
                </Box>
              )}
            </Box>
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} color="primary" />}
              label={<Typography style={{ fontSize: '0.88rem', fontWeight: 500 }}>Organização ativa</Typography>}
            />

            {/* [V3] Os módulos e a configuração NÃO se definem na Organização —
                vêm do PERFIL que ela aponta (acima). Nota didática: */}
            <Box style={{ background: 'var(--adm-brand-soft, #eef5f1)', border: '1px solid #d8e9e1', borderRadius: 12, padding: '12px 16px' }}>
              <Typography style={{ fontSize: '0.78rem', color: '#3f6b5c', fontWeight: 500, lineHeight: 1.5 }}>
                Os módulos, preferências e fluxo das OT vêm do Perfil escolhido acima. Cada Organização tem o seu próprio Perfil. Para os alterar, edita o Perfil na página Perfis — ou altera as Preferências dentro do planner, que escrevem no mesmo sítio.
              </Typography>
            </Box>

            {/* Default origin removed — now managed via garage locations */}
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setDialogOpen(false)} className={classes.cancelBtn}>{activeTab === 'dados' ? 'Cancelar' : 'Fechar'}</Button>
          {activeTab === 'dados' && (
            <Button onClick={handleSave} variant="contained" className={classes.saveBtn} disableElevation disabled={saving || !form.name || !form.slug}>
              {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminClients;
